/*
 * We use Segment to capture interesting events in our data:
 *
 * - identify (the selected User)
 * - group (the selected Organization)
 * - page (one of several "pages" we identified as interesting)
 * - track (one of many tasks we identified as interesting)
 *
 * These events are typically sent from a React component -- but because React components can easily be
 * called many times, and we don't want to send multiple Segments events for what the user perceives as
 * one action, we track the last time we made a call with the same parameters and only send it again
 * only if the parameters have changed or "enough" time has gone by
 *
 * For now, "enough" time is an hour, so it will only trigger for this reason if the user stays on
 * the same page for an hour. This could be wrong
 *
 * There are separate caches for each event we send to Segment, to prevent situations like
 * adding a tag in the collaboration window, which triggers "tag added" but then, because the
 * state has changed rerenders the collaboration, which we don't want to report to Segment.
 * There is a special case for 'group' because there is effectively only one event for group,
 * which happens when you change organization.
 *
 * NOTE: this design may not be correct in the long-run. If we ever want PRECISE information
 * about these events rather than this somewhat vague effort to send events only when "reasonable,"
 * we probably will need logic at each point where we send Segment data that decides if the event should really be sent.
 * At the moment, a roughly-correct value for events is all we need
 *
 * SegmentEventName = the value reported to Segment, right now:
 *
 * Pages
 *
 * Track
 *
 *   collaboration created
 *   collaboration viewed
 *   media uploaded
 *   media viewed
 *   tag added
 *   task report created
 *   media report created
 *   project tasklist
 *   project media viewed
 *   canvas viewed
 *   organization projects viewed
 *
 * Group
 *
 *   group (this is a special case; the group we report is always an organization id, not the word "group")
 *
 */

import { Category, StatusName } from '@range.io/basic-types'
import { assoc, equals, tagged } from '@range.io/functional'
import { generateIntercomUserHash } from '../firebase/commands/https-calls.js'
import ReduxSelectors from '../redux/redux-selectors.js'
import getUtmAttributionParams from './getUtmAttributionParams.js'

// ---------------------------------------------------------------------------------------------------------------------
// Caching
// ---------------------------------------------------------------------------------------------------------------------

// set to a very long time for now
const MILLISECONDS_PER_SECOND = 1000
const SECONDS_PER_HOUR = 3600
const minimumDelayBetweenCalls = SECONDS_PER_HOUR * MILLISECONDS_PER_SECOND

/*
 * Track a single previous call
 */
const PreviousCall = tagged('PreviousCall', {
    previousKey: 'String', // name of the call (eg. "canvas", "tasklist", or some other identifier)
    previousParams: 'Object', // depends on caller
    previousCallTime: 'Number', // Date.now()
})

// cached by SegmentEventName
const previousCalls = {}

/*
 * Returns true if the call to Segment SHOULD be made
 * @sig shouldSendCallToSegment :: (SegmentEventName, String, {k:v}) -> Boolean
 */
const shouldSendCallToSegment = (segmentEventName, key, params) => {
    const previousCall = previousCalls[segmentEventName] || {}
    const { previousKey, previousParams, previousCallTime } = previousCall

    const nameChanged = previousKey !== key
    const sentLongAgo = Date.now() - minimumDelayBetweenCalls > previousCallTime
    const paramsChanged = !equals(params, previousParams)
    const shouldSendToSegment = nameChanged || sentLongAgo || paramsChanged

    // same the new previous value for next time
    if (shouldSendToSegment) previousCalls[segmentEventName] = PreviousCall(key, params, Date.now())

    return shouldSendToSegment
}

// ---------------------------------------------------------------------------------------------------------------------
// Specific calls to Segment (each protected by its own cache)
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Makes a call to analytics.page if the name or params have changed
 * You're only ever on 1 page at a time, so all the page calls share the same PreviousCall value
 */
const sendPage = (name, id, params) => {
    const key = name + id
    if (shouldSendCallToSegment(name, key, params)) window?.analytics?.page?.(name, assoc('title', name, params))
}

/*
 * Makes a call to analytics.track if the name, id or params have changed. id is used only to trigger a new write,
 * if you want to send it to Segment, include it in the params as well
 */
const sendTrack = (name, id, params) => {
    const key = name + id
    if (shouldSendCallToSegment(name, key, params)) {
        const context = { groupId: params.groupId }

        window?.analytics?.track?.(name, params, { context })
    }
}

/*
 * Makes a call to analytics.group if the organization or user's organizationRole has changed
 * You're only ever in 1 group at a time
 * @sig sendGroup = (Organization, String) -> ()
 */
const sendGroup = (organization, organizationRole) => {
    const { id, name, industry, size = '10-50' } = organization

    const utmAttribution = getUtmAttributionParams()
    const params = { name, industry, size, organizationRole, ...utmAttribution }
    if (shouldSendCallToSegment('group', id, params)) window?.analytics?.group?.(id, params)
}

/*
 * Make a call to analytics.identify
 */
const sendIdentify = async user => {
    const data = {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        language: user.language,
        jobTitle: user.role,
    }

    const { userHash } = await generateIntercomUserHash()

    const options = {
        integrations: {
            Intercom: {
                user_hash: userHash,
            },
        },
    }

    window?.analytics?.identify?.(user.id, data, options)
}

const reset = () => window?.analytics?.reset?.()

/*
 * Sends a Segment track event for when category is assigned to a collaboration.
 * Happens only when the category is different then the default one.
 */
const sendCategoryAddedSegmentEvent = (categoryId, collaboration, getState) => {
    const category = ReduxSelectors.itemWithId(getState(), Category, categoryId)
    // only send when assigning non-default category
    if (!category.isDefault) {
        const trackParams = ReduxSelectors.paramsForTrackEvent(getState())
        trackParams.categoryName = category.name
        const selectedProject = ReduxSelectors.selectedProject(getState())
        trackParams.projectName = selectedProject.name
        const { canvasSource } = ReduxSelectors.canvasAndFirstSourceForId(collaboration.canvasId)(getState())
        trackParams.canvasType = canvasSource.type
        trackParams.collaborationTitle = collaboration.name
        trackParams.collaborationType = collaboration.statusNameId ? 'task' : 'photo'
        if (trackParams.collaborationType === 'task') {
            const statusName = ReduxSelectors.itemWithId(getState(), StatusName, collaboration.statusNameId)
            trackParams.taskStatus = statusName.name
        }
        sendTrack('category added', collaboration.id, trackParams)
    }
}

export { sendIdentify, sendPage, sendGroup, sendTrack, reset, sendCategoryAddedSegmentEvent }
